import { PayPeriod } from '../../interfaces/PayPeriodInterface';

/**
 * Date and status helpers for the Weekly Safety Message page (TAS-128).
 *
 * EVERYTHING HERE IS UTC. Pay period StartDate/EndDate arrive from the API as
 * midnight-UTC ISO strings; formatting them in the viewer's zone renders every
 * week label one day early for anyone west of Greenwich, which is every user of
 * this product. Same reasoning as miscTaskHelpers.ts:83-89. Date-only values are
 * anchored at midnight UTC and formatted with timeZone: 'UTC'.
 *
 * PAY PERIOD START DAY IS DATA. 20/20 P&H runs Mon→Sun, NP Mechanical Thu→Wed.
 * Every label below renders from StartDate/EndDate — nothing here assumes Monday.
 */

/** `2026-08-10T00:00:00.000Z` | Date | `2026-08-10` → `2026-08-10`. */
export function ymd(value: string | Date): string {
  if (value instanceof Date) {
    return `${value.getUTCFullYear()}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(
      value.getUTCDate()
    ).padStart(2, '0')}`;
  }
  return String(value).slice(0, 10);
}

/**
 * Today as a local `YYYY-MM-DD`.
 *
 * Local, not UTC, and computed per call rather than once at module load: this is
 * "which week is the admin sitting in right now", and a tab left open across
 * midnight should not keep highlighting yesterday's week.
 */
export function todayYmd(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate()
  ).padStart(2, '0')}`;
}

function utcDate(value: string | Date): Date {
  return new Date(`${ymd(value)}T00:00:00Z`);
}

/**
 * `Mon Aug 10 – Sun Aug 16`.
 *
 * Deliberately does NOT collapse a same-month range the way WeekPickerNav's
 * fmtRange does: the weekday names are the point here — they are what tells an
 * admin at a glance which day this tenant's week turns over.
 */
export function weekRangeLabel(start: string | Date, end: string | Date): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  };
  return `${utcDate(start).toLocaleDateString('en-US', opts)} – ${utcDate(end).toLocaleDateString(
    'en-US',
    opts
  )}`;
}

/** `Fri Aug 7` — for the "last edited" column and the editor header. */
export function shortDayLabel(value: string | Date): string {
  return utcDate(value).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** Whole weeks from `fromStart` to `toStart`, positive when `toStart` is later. */
function weeksBetween(fromStart: string | Date, toStart: string | Date): number {
  const ms = utcDate(toStart).getTime() - utcDate(fromStart).getTime();
  return Math.round(ms / (7 * 24 * 60 * 60 * 1000));
}

/**
 * `This week` / `Next week` / `In 3 weeks` / `Last week` / `5 weeks ago`.
 *
 * Hand-written rather than Intl.RelativeTimeFormat: that returns lowercase
 * ("next week", "in 3 weeks"), and these strings are sentence-case labels in a
 * table column, so it would need re-capitalising anyway.
 */
export function relativeWeekLabel(periodStart: string | Date, currentStart: string | Date): string {
  const n = weeksBetween(currentStart, periodStart);
  if (n === 0) return 'This week';
  if (n === 1) return 'Next week';
  if (n === -1) return 'Last week';
  return n > 0 ? `In ${n} weeks` : `${Math.abs(n)} weeks ago`;
}

/**
 * Is today inside this pay period?
 *
 * String comparison on `YYYY-MM-DD`, the approach PayPeriods.tsx:64-68 uses.
 * Deliberately not a Date comparison: WeekPickerNav.tsx:371-387 documents a real
 * ~24h dead window that came from comparing a live timestamp against
 * noon-UTC-anchored boundaries, where no period matched at all.
 */
export function isCurrentPeriod(pp: PayPeriod, today = todayYmd()): boolean {
  return today >= ymd(pp.StartDate) && today <= ymd(pp.EndDate);
}

/** Where a pay period sits relative to today. */
export type PeriodPosition = 'current' | 'future' | 'past';

export function periodPosition(pp: PayPeriod, today = todayYmd()): PeriodPosition {
  if (isCurrentPeriod(pp, today)) return 'current';
  return ymd(pp.StartDate) > today ? 'future' : 'past';
}

/**
 * The status pill.
 *
 * Status describes TIME, not workflow — there is no draft state, so a saved
 * message is always live-or-scheduled and the only question is where its week
 * sits relative to today.
 */
export type SafetyStatus = 'live' | 'ready' | 'sent' | 'none';

export function safetyStatus(position: PeriodPosition, hasMessage: boolean): SafetyStatus {
  if (!hasMessage) return 'none';
  if (position === 'current') return 'live';
  return position === 'future' ? 'ready' : 'sent';
}

export const STATUS_LABEL: Record<SafetyStatus, string> = {
  live: 'Live',
  ready: 'Ready',
  sent: 'Sent',
  none: 'None',
};
